/**
 * map-moq.js — minimum order quantities and variant prices in the store
 * locator's "add to quote" modal (sections/store-locator.liquid).
 *
 * The map engine (assets/map-<engine>.js, minified, no sources in the repo)
 * opens its variant selector at quantity 1 and shows the product's lowest
 * price. Minimum order quantities live in the "N exemplaires" product tag and
 * Alfred wants them respected everywhere (round 3, 2026-09-10, see CHANGELOG).
 * Rather than patching four minified bundles, this overlay watches the section
 * and corrects the modal after the engine has built it:
 *
 *  - remembers the card whose "Ajouter au devis" button was clicked;
 *  - when the modal's quantity input appears, fetches /products/<handle>.js
 *    (once per handle, shared cache) and applies the minimum: min= and value
 *    on the input, a "Minimum : N exemplaires" hint (text editable in the
 *    theme editor via data-moq-hint on the section), and a clamp on typing,
 *    on "-" and on "+";
 *  - once every option is chosen, writes the chosen variant's unit price
 *    ("X.XX € HT") into the modal's price slot;
 *  - prefixes the card price with "À partir de " when the product's price
 *    varies, so the lowest price reads as a from-price.
 *
 *  - round 3c: no correction while typing (typing "100" on a 50 minimum
 *    must not snap to 50 at the first keystroke): the value is clamped on
 *    change/blur, after "-", and right before the final button's engine
 *    handler; meanwhile a below-minimum value only gets an "is-below-min"
 *    outline.
 *  - round 3b: products the engine adds WITHOUT a modal (no variants, or a
 *    single "Default Title" variant) are intercepted on the card click in the
 *    capture phase and added here at their minimum with the variant's price,
 *    then redirected like the engine would; the products of a popup are
 *    prefetched as soon as the popup opens so the click needs no wait.
 *
 * Quote mode only ("Achat en volume / personnalisation"); the unit-purchase
 * cart path is left to the engine. Any fetch failure leaves the engine's
 * behaviour untouched. The engine still stores what it stores; the quote page
 * (assets/quotePage.js, round 2) re-reads price and minimum on arrival, so the
 * two layers agree.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-store-locator]';
  var CARD_BUTTON = '.btn-add';
  var QTY_INPUT = '.variant-selector__qty-input';
  var QTY_DECREASE = '.variant-selector__qty-decrease';
  var QTY_INCREASE = '.variant-selector__qty-increase';
  var OPTION = '.variant-selector__option';
  var PRICE = '.variant-selector__price';
  var QTY_ROW = '.variant-selector__qty-row';
  var FINAL_BUTTON = '.variant-selector__final-button';
  var BELOW_MIN_CLASS = 'is-below-min';
  var HINT_CLASS = 'variant-selector__moq-hint';
  var FROM_PREFIX = 'À partir de ';
  var MIN_TAG = /^(\d+)\s*exemplaires$/i;
  var STORAGE_KEY = 'quoteItems';
  var CHANGE_EVENT = 'lcf:quote:changed';
  var QUOTE_PAGE_URL = '/pages/demande-de-devis';

  var products = {};                 // handle -> Promise<product json | null>
  var last = { handle: null, mode: null };

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function minimumFromTags(tags) {
    var list = Array.isArray(tags) ? tags : String(tags || '').split(',');
    for (var i = 0; i < list.length; i++) {
      var match = MIN_TAG.exec(String(list[i]).trim());
      if (match) return Math.max(1, toInt(match[1], 1));
    }
    return 1;
  }

  function formatHT(cents) {
    return (cents / 100).toFixed(2) + ' € HT';
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!products[handle]) {
      products[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(function (response) {
        return response.ok ? response.json() : null;
      }).catch(function () {
        return null;
      });
    }
    return products[handle];
  }

  function findVariant(product, values) {
    var variants = (product && product.variants) || [];
    var title = values.join(' / ');
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].title === title) return variants[i];
    }
    for (var j = 0; j < variants.length; j++) {
      var options = variants[j].options || [];
      if (options.length === values.length && options.every(function (o, k) { return o === values[k]; })) {
        return variants[j];
      }
    }
    return null;
  }

  function hintText(section, min) {
    var template = (section && section.getAttribute('data-moq-hint')) || 'Minimum : {min} exemplaires';
    return template.replace(/\{\{?\s*min\s*\}?\}/g, String(min));
  }

  function quoteMode() {
    if (last.mode) return last.mode === 'quote';
    var toggle = document.getElementById('client_type_switch');
    return !!(toggle && toggle.checked);
  }

  /* ------------------------------------------------------------------ */
  /* Direct add (no modal): same item shape as productQuote.js           */
  /* ------------------------------------------------------------------ */

  function decodeAttr(button, name) {
    try { return decodeURIComponent(button.getAttribute(name) || ''); } catch (error) { return button.getAttribute(name) || ''; }
  }

  function cardVariants(button) {
    try {
      var raw = button.getAttribute('data-product-variants');
      var list = raw ? JSON.parse(decodeURIComponent(raw)) : [];
      return Array.isArray(list) ? list : [];
    } catch (error) {
      return null;
    }
  }

  // The engine skips its modal when the product has no variants or a single
  // default one; those are the clicks this overlay has to take over.
  function skipsModal(variants) {
    if (!variants) return false;
    if (variants.length === 0) return true;
    return variants.length === 1 && (variants[0].title === 'Default Title' || variants[0].title === 'TITLE');
  }

  function readItems() {
    try {
      var stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter(function (i) { return i && i.variantId != null; }) : [];
    } catch (error) {
      return [];
    }
  }

  function addItem(item) {
    var items = readItems();
    var merged = false;
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variantId) === item.variantId) {
        items[i].qty = (parseInt(items[i].qty, 10) || 0) + item.qty;
        if (!(items[i].minQty > item.minQty)) items[i].minQty = item.minQty;
        items[i].price = item.price;
        items[i].total = Math.round(items[i].price * items[i].qty * 100) / 100;
        items[i].verifiedAt = item.verifiedAt;
        merged = true;
      }
    }
    if (!merged) items.push(item);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      return false;
    }
    try { document.dispatchEvent(new CustomEvent(CHANGE_EVENT)); } catch (error) { /* old browser */ }
    return true;
  }

  function directAdd(button, variants) {
    var handle = button.getAttribute('data-product-handle');
    var name = decodeAttr(button, 'data-product-title');
    var image = decodeAttr(button, 'data-product-image');
    var cardPrice = parseFloat(button.getAttribute('data-product-price')) || 0;
    var cardVariantId = variants.length ? variants[0].id : button.getAttribute('data-product-id');

    fetchProduct(handle).then(function (product) {
      var item;
      var variant = product && product.variants && product.variants[0];
      if (product && variant) {
        var min = minimumFromTags(product.tags);
        var price = typeof variant.price === 'number' ? variant.price / 100 : cardPrice;
        item = {
          variantId: String(variant.id),
          name: product.title || name,
          variantTitle: variant.title || null,
          variantOptions: variant.options || [],
          price: price,
          handle: handle,
          image: image,
          qty: min,
          minQty: min,
          verifiedAt: Date.now(),
          total: Math.round(price * min * 100) / 100
        };
      } else {
        // Product JSON unavailable: do exactly what the engine would have done.
        item = {
          variantId: String(cardVariantId), name: name, variantTitle: null, variantOptions: [],
          price: cardPrice, handle: handle, image: image, qty: 1, minQty: 1, verifiedAt: 0, total: cardPrice
        };
      }
      addItem(item);
      window.location.href = QUOTE_PAGE_URL;
    });
  }

  /* ------------------------------------------------------------------ */
  /* The modal                                                           */
  /* ------------------------------------------------------------------ */

  function enhanceModal(section, input) {
    if (input.dataset.moqBound) return;
    input.dataset.moqBound = '1';
    if (!quoteMode()) return;

    var modal = input.closest('.variant-selector') || section;
    var handle = last.handle;

    fetchProduct(handle).then(function (product) {
      if (!product || !modal.contains(input)) return;

      var min = minimumFromTags(product.tags);
      var priceSlot = modal.querySelector(PRICE);
      var selects = Array.prototype.slice.call(modal.querySelectorAll(OPTION));

      function clamp() {
        var value = toInt(input.value, 0);
        if (value < min) input.value = String(min);
        input.classList.remove(BELOW_MIN_CLASS);
      }

      // While typing, leave the value alone (typing "100" must not become
      // "50" at the first keystroke); only flag it.
      function flag() {
        var value = toInt(input.value, 0);
        input.classList.toggle(BELOW_MIN_CLASS, value < min);
      }

      function showUnitPrice() {
        if (!priceSlot || !selects.length) return;
        var values = selects.map(function (s) { return s.value; });
        if (values.some(function (v) { return v === ''; })) return;
        var variant = findVariant(product, values);
        if (variant && typeof variant.price === 'number') priceSlot.textContent = formatHT(variant.price);
      }

      if (min > 1) {
        input.min = String(min);
        clamp();
        var row = modal.querySelector(QTY_ROW);
        if (row && !modal.querySelector('.' + HINT_CLASS)) {
          var hint = document.createElement('div');
          hint.className = HINT_CLASS;
          hint.textContent = hintText(section, min);
          row.parentNode.insertBefore(hint, row.nextSibling);
        }
        // Registered after the engine's own handlers, so these run once the
        // engine has applied its change and put the value back if needed.
        input.addEventListener('input', flag);
        input.addEventListener('change', clamp);
        input.addEventListener('blur', clamp);
        var decrease = modal.querySelector(QTY_DECREASE);
        if (decrease) decrease.addEventListener('click', function () { clamp(); showUnitPrice(); });
        // Capture phase on the modal runs before the engine's listener on the
        // button, so a value still below the minimum is corrected first.
        modal.addEventListener('click', function (event) {
          if (event.target.closest(FINAL_BUTTON)) clamp();
        }, true);
      }

      var increase = modal.querySelector(QTY_INCREASE);
      if (increase) increase.addEventListener('click', showUnitPrice);
      input.addEventListener('input', showUnitPrice);
      input.addEventListener('change', showUnitPrice);
      selects.forEach(function (s) { s.addEventListener('change', showUnitPrice); });
      showUnitPrice();
    });
  }

  /* ------------------------------------------------------------------ */
  /* The cards                                                           */
  /* ------------------------------------------------------------------ */

  function decorateCard(button) {
    if (button.dataset.moqSeen) return;
    button.dataset.moqSeen = '1';
    fetchProduct(button.getAttribute('data-product-handle'));   // warm the cache for the click
    if (!/devis/i.test(button.textContent)) return;   // cart mode: leave it
    var card = button.closest('.slider-item') || button.parentNode;
    var priceEl = null;
    var paragraphs = card ? card.querySelectorAll('p') : [];
    for (var i = 0; i < paragraphs.length; i++) {
      if (paragraphs[i].textContent.indexOf('€') !== -1) { priceEl = paragraphs[i]; break; }
    }
    if (!priceEl) return;
    fetchProduct(button.getAttribute('data-product-handle')).then(function (product) {
      if (!product || !product.price_varies) return;
      var text = priceEl.textContent.trim();
      if (text.indexOf(FROM_PREFIX.trim()) === 0) return;
      priceEl.textContent = FROM_PREFIX + text;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Wiring                                                              */
  /* ------------------------------------------------------------------ */

  function scan(section, node) {
    if (!node || node.nodeType !== 1) return;
    var buttons = node.matches && node.matches(CARD_BUTTON) ? [node] : node.querySelectorAll(CARD_BUTTON);
    for (var i = 0; i < buttons.length; i++) decorateCard(buttons[i]);
    var inputs = node.matches && node.matches(QTY_INPUT) ? [node] : node.querySelectorAll(QTY_INPUT);
    for (var j = 0; j < inputs.length; j++) enhanceModal(section, inputs[j]);
  }

  function init() {
    var section = document.querySelector(SECTION_SELECTOR);
    if (!section || typeof MutationObserver === 'undefined' || typeof fetch !== 'function') return;

    section.addEventListener('click', function (event) {
      var button = event.target.closest(CARD_BUTTON);
      if (!button) return;
      last.handle = button.getAttribute('data-product-handle');
      last.mode = /devis/i.test(button.textContent) ? 'quote' : 'cart';
      if (last.mode !== 'quote') return;                       // cart path: engine only
      var variants = cardVariants(button);
      if (!skipsModal(variants) || !last.handle) return;       // modal path: handled above
      try {
        // Capture phase runs before the engine's own listener on the button.
        event.stopPropagation();
        event.preventDefault();
        directAdd(button, variants);
      } catch (error) { /* never break the map */ }
    }, true);

    new MutationObserver(function (mutations) {
      try {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) scan(section, added[j]);
        }
      } catch (error) { /* never break the map */ }
    }).observe(section, { childList: true, subtree: true });

    scan(section, section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
